import * as Sentry from '@sentry/react';
import { Box, Flex, Heading, useToast, VStack } from '@televet/kibble-ui';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { useTracking } from 'shared/providers/TrackingProvider';
import React, { useState } from 'react';
import Switch from 'shared/components/Switch';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ChannelVisibility, useCreateTeamChannelMutation } from 'shared/types/graphql';
import ParticipantsInput from './ParticipantsInput';
import { TeamChatEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IAddTeamChannelModalProps {
  onSuccess: (newChannelId: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

const AddTeamChannelModal = ({ onSuccess, isOpen, onClose }: IAddTeamChannelModalProps): JSX.Element => {
  const [friendlyName, setFriendlyName] = useState('');
  const [friendlyNameValidationMessage, setFriendlyNameValidationMessage] = useState('');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [isChannelPrivate, setIsChannelPrivate] = useState<boolean>(false);
  const { clinicUser } = useClinicUser();
  const toast = useToast();
  const [createTeamChannel, { loading: isCreatingTeamChannel }] = useCreateTeamChannelMutation();
  const { trackEvent } = useTracking();

  const handleAddChannelClick = async (): Promise<void> => {
    if (!friendlyName) {
      setFriendlyNameValidationMessage('Please provide a name for this channel');
      return;
    }

    try {
      const result = await createTeamChannel({
        variables: {
          data: {
            friendlyName,
            assigneeIds: [...assigneeIds, clinicUser?.id || ''],
            channelVisibility: isChannelPrivate ? ChannelVisibility.Private : ChannelVisibility.Public,
          },
        },
      });
      const newChannelId = result?.data?.createTeamChannel2?.id;
      if (!newChannelId) {
        throw new Error('Error creating team channel');
      }
      toast({
        status: 'success',
        description: `Success! The ${friendlyName} team channel was added to the clinic`,
        duration: 5000,
      });

      trackEvent(TeamChatEvents.PublicChannelCreated, {
        channelVisibility: result?.data?.createTeamChannel2?.channelVisibility || '',
      });
      onSuccess(newChannelId);
    } catch (e) {
      Sentry.captureException(e);
      toast({
        status: 'error',
        description:
          'Whoops! An error occurred while adding the ${friendlyName} team channel to the clinic. Please try again.',
        duration: 5000,
      });
      console.error(e);
    } finally {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader pb={1}>
        <Heading size="sm" mb={4}>
          Add Team Channel
        </Heading>
        <Text size="sm" fontWeight="normal">
          Add a channel to start a conversation around a specific team, topic, or workflow with colleagues at your
          clinic.
        </Text>
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody>
        <VStack spacing={4} alignItems="stretch">
          <TextInput
            name="friendlyName"
            label="Channel Name"
            aria-label="friendlyName"
            autoFocus={true}
            value={friendlyName}
            isRequired={true}
            labelStyle={{
              fontWeight: 'bold',
              fontSize: 'sm',
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => setFriendlyName(event.target.value)}
            isInvalid={!!friendlyNameValidationMessage}
            errorText={friendlyNameValidationMessage}
          />
          <Box>
            <Text fontSize="sm" fontWeight="bold" mb={1}>
              Make private?
            </Text>
            <Flex>
              <Text as="p" mb={0} minWidth="70%" fontSize="sm" pr={4}>
                When a channel is set to private, it can only be viewed or joined by invitation. You can still invite
                users to join after you&apos;ve created the channel.
              </Text>
              <Box width="100%" zIndex={0}>
                <Switch
                  name="channelPrivacy"
                  label=""
                  checked={isChannelPrivate}
                  onChange={(): void => setIsChannelPrivate(!isChannelPrivate)}
                />
              </Box>
            </Flex>
          </Box>
          <ParticipantsInput selectedUserIds={assigneeIds} onChange={setAssigneeIds} />
        </VStack>
        <ModalFooterButtons
          px={0}
          submitText="Add Channel"
          onSubmit={handleAddChannelClick}
          onCancel={onClose}
          isLoading={isCreatingTeamChannel}
        />
      </ModalBody>
    </Modal>
  );
};

export default AddTeamChannelModal;
